import { zodResolver } from '@hookform/resolvers/zod';
import { Fragment, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';
import { Button } from '@/components/ui/Button';
import { useRiskRangeUpdateMutation } from '@/features/biometric/hooks/use-risk-range-mutations';
import { useRiskRangeQuery } from '@/features/biometric/hooks/use-risk-range';
import {
  RiskRangeApiError,
  type RiskRange,
} from '@/features/biometric/types/risk-range.types';
import {
  DEFAULT_RISK_RANGE,
  RISK_BOUND_KEYS,
  RISK_INTERVAL_KEYS,
  RISK_RANGE_STEP,
} from '@/features/biometric/utils/risk-range.defaults';
import { riskRangeSchema } from '@/features/biometric/utils/risk-range.schema';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

type FormValues = RiskRange;

// Errors that belong to ONE field only. Everything else the schema raises (interval order, adjacency)
// is a rule about several fields.
//
// This split drives two separate things, which is why it is one list:
//  - SHCT-811: only a multi-field rule can go stale on a field the user is not editing, so only those
//    need the revalidate-everything pass.
//  - SHCT-362/681: PRD 09 §8.1 gives the two kinds different homes — a single-field failure belongs
//    "해당 필드 하단", a multi-field one "해당 카드 에러 영역". Both used to land in the card area, and only
//    the first of them was ever printed, so a field could be outlined red with its reason nowhere on
//    screen (a Medium 하한 breaking continuity while a Low min>max message held the one slot).
const SINGLE_FIELD_ERRORS: ReadonlySet<string> = new Set([
  'riskRange.errors.required',
  'riskRange.errors.minValue',
  'riskRange.errors.decimalPlaces',
]);

const INTERVALS = ['low', 'mid', 'high'] as const;
// Ordered field list — drives is-error styling and the order the card-level messages are listed in.
const FIELD_PATHS = [
  ['low', 'min'],
  ['low', 'max'],
  ['mid', 'min'],
  ['mid', 'max'],
  ['high', 'min'],
  ['high', 'max'],
] as const;

/**
 * Copy for a failed load or save, shared by both of this card's failure surfaces.
 *
 * A NETWORK error is the one system failure the operator can act on, so it keeps its own line
 * instead of hiding behind the fixed "could not load / could not save" text; RISK_BAND_NOT_CONTINUOUS
 * is the server's own actionable message. Everything else is the caller's fixed copy — an unmodelled
 * server code must not reach the screen (same rule as the adapter's mapError).
 */
function riskRangeErrorMessage(
  err: unknown,
  t: (key: string) => string,
  fallbackKey: string,
): string {
  if (err instanceof RiskRangeApiError) {
    if (err.code === 'NETWORK') return t('riskRange.errors.NETWORK');
    if (err.code === 'RISK_BAND_NOT_CONTINUOUS') return err.message;
  }
  return t(fallbackKey);
}

interface FormProps {
  serverValues: RiskRange;
  canWrite: boolean;
}

function RiskRangeForm({ serverValues, canWrite }: FormProps) {
  const { t } = useTranslation('admin');
  // SHCT-582 — PRD 21 §7.4 P-2: footer stays and goes `disabled` + 툴팁 instead of vanishing.
  const { denyProps } = useWriteDenyGuard(canWrite);
  const mutation = useRiskRangeUpdateMutation();

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(riskRangeSchema),
    defaultValues: serverValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Sync server data into form when it changes (e.g. after invalidateQueries).
  useEffect(() => {
    reset(serverValues);
  }, [serverValues, reset]);

  const fieldError = (interval: string, bound: string) =>
    errors[interval as keyof FormValues]?.[bound as 'min' | 'max']?.message;

  // PRD 09 §8.1 — a single-field failure is printed under its own input.
  const inlineError = (interval: string, bound: string) => {
    const key = fieldError(interval, bound);
    return typeof key === 'string' && SINGLE_FIELD_ERRORS.has(key)
      ? t(key)
      : null;
  };

  // PRD 09 §8.1 — the multi-field rules (interval order, adjacency) have no single field to sit under,
  // so they go to the card's error area. ALL of them are listed, deduped: one rule can raise on two
  // inputs at once, and two different rules can be broken at the same time — printing only the first
  // left the other's field red and unexplained.
  const cardErrors = [
    ...new Set(
      FIELD_PATHS.map(([i, b]) => fieldError(i, b)).filter(
        (key): key is string =>
          typeof key === 'string' && !SINGLE_FIELD_ERRORS.has(key),
      ),
    ),
  ].map((key) => t(key));

  const hasError = FIELD_PATHS.some(([i, b]) => Boolean(fieldError(i, b)));

  function onReset() {
    // Restore the default seed on-screen only (PRD 09 §3.3). Keep server values as the dirty
    // baseline so Save enables when defaults differ from the saved values and stays disabled
    // when they already match.
    reset(DEFAULT_RISK_RANGE, { keepDefaultValues: true });
  }

  // Synchronous in-flight lock: blocks a rapid double-click from firing a second save
  // before mutation.isPending flips and disables the button.
  const savingRef = useRef(false);

  function onSave(values: FormValues) {
    if (savingRef.current) return;
    savingRef.current = true;
    mutation.mutate(values, {
      onSuccess: () => {
        toast.success(t('biometricData.toasts.saved'));
        // Make the saved values the clean baseline immediately so isDirty flips to false
        // now, not when the invalidateQueries refetch lands.
        reset(values);
      },
      onError: (err) => {
        // Three outcomes, in order of how much the user can act on them:
        //  - a continuity conflict rejected server-side carries an actionable message;
        //  - a request that never reached the server is a connection problem, and saying so
        //    is the difference between "retry now" and "the save was refused";
        //  - anything else is a system failure behind the standard save-failure toast.
        toast.error(riskRangeErrorMessage(err, t, 'biometricData.toasts.saveError'), {
          ttlMs: 5000,
        });
      },
      onSettled: () => {
        savingRef.current = false;
      },
    });
  }

  // SHCT-811 (same defect as the 4-threshold cards) — the ordering and continuity rules span several
  // fields, so they raise their issue on several fields at once, but React Hook Form writes back only
  // the error of the field it is validating. The others kept a stale message: correcting the boundary
  // from a DIFFERENT input cleared that input alone and 저장 stayed disabled until the user went back
  // and touched a field that was already valid. While such an error is on screen, editing any of the
  // six revalidates all six; with no error there is nothing stale to clear, and revalidating every
  // keystroke would surface errors mid-typing, which this form's `mode: 'onBlur'` avoids.
  const hasCrossFieldError = RISK_INTERVAL_KEYS.some((interval) =>
    RISK_BOUND_KEYS.some((bound) => {
      const message = fieldError(interval, bound);
      return typeof message === 'string' && !SINGLE_FIELD_ERRORS.has(message);
    }),
  );

  const registerBound = (interval: (typeof RISK_INTERVAL_KEYS)[number], bound: 'min' | 'max') =>
    register(`${interval}.${bound}` as const, {
      valueAsNumber: true,
      onChange: () => {
        if (hasCrossFieldError) void trigger();
      },
    });

  const inputClass = (interval: string, bound: string) =>
    `bio-card__input${fieldError(interval, bound) ? ' is-error' : ''}`;

  // type="number" natively accepts scientific notation (3e3) and signs; restrict typed/pasted
  // characters to digits and the decimal point.
  const allowedChars = /^[0-9.]+$/;
  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const { data } = e.nativeEvent as InputEvent;
    if (data != null && !allowedChars.test(data)) {
      e.preventDefault();
    }
  };

  return (
    <div className="bio-card">
      <div className="bio-card__header">
        <h2 className="bio-card__title">{t('riskRange.title')}</h2>
        <span className="bio-card__unit-badge">{t('riskRange.unit')}</span>
      </div>

      <form onSubmit={handleSubmit(onSave)} noValidate>
        {INTERVALS.map((interval) => (
          <section key={interval} className="bio-card__section">
            <div className="bio-card__section-title">
              {t(`riskRange.intervals.${interval}`)}
            </div>
            <div className="bio-card__row">
              {RISK_BOUND_KEYS.map((bound, i) => (
                <Fragment key={bound}>
                  {i > 0 && (
                    <span className="bio-card__connector">
                      {t('riskRange.labels.connector')}
                    </span>
                  )}
                  <div className="bio-card__input-group">
                    <span>{t(`riskRange.labels.${bound}`)}</span>
                    <input
                      type="number"
                      step={RISK_RANGE_STEP}
                      className={inputClass(interval, bound)}
                      disabled={!canWrite}
                      {...denyProps}
                      onBeforeInput={handleBeforeInput}
                      {...registerBound(interval, bound)}
                    />
                    {/* PRD 09 §8.1 "해당 필드 하단" — this input's own failure, under this input. */}
                    {inlineError(interval, bound) && (
                      <p className="form-field__error">
                        {inlineError(interval, bound)}
                      </p>
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
          </section>
        ))}

        {/* PRD 09 §8.1 "해당 카드 에러 영역" — the rules that span fields, every one of them. */}
        <div className="bio-card__error-msg" role="alert">
          {cardErrors.map((message) => (
            <div key={message}>{message}</div>
          ))}
        </div>

        <div className="bio-card__footer content__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={onReset}
            disabled={!canWrite || mutation.isPending}
            {...denyProps}
          >
            {t('biometricData.actions.reset')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canWrite || hasError || mutation.isPending || !isDirty}
            {...denyProps}
          >
            {mutation.isPending
              ? t('biometricData.actions.saving')
              : t('biometricData.actions.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface Props {
  canWrite: boolean;
}

// Self-contained card: owns its query so a biometric-ranges failure does not hide the R-value
// card and vice versa. Slots into the 데이터기준값관리 grid as the 4th card (PRD 09 §2.1).
export function RiskRangeCard({ canWrite }: Props) {
  const { t } = useTranslation('admin');
  const { data, isLoading, isError, error, refetch, isFetching } =
    useRiskRangeQuery();

  if (isLoading) {
    return <div className="bio-card bio-card--skeleton" aria-busy="true" />;
  }

  if (isError || !data) {
    return (
      <div className="bio-card biometric-data__error" role="alert">
        <p>{riskRangeErrorMessage(error, t, 'riskRange.toasts.loadError')}</p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching
            ? t('biometricData.actions.retrying')
            : t('biometricData.actions.retry')}
        </Button>
      </div>
    );
  }

  return <RiskRangeForm serverValues={data} canWrite={canWrite} />;
}
